import Dialog from "@bmi-digital/components/dialog";
import { replaceSpaces, transformHyphens } from "@bmi-digital/components/utils";
import classnames from "classnames";
import { graphql, Link as GatsbyLink } from "gatsby";
import uniqueId from "lodash-es/uniqueId";
import React, { Suspense, useCallback, useContext, useState } from "react";
import { getPathWithCountryCode } from "../../utils/path";
import { CalculatorContext } from "../PitchedRoofCalculator";
import { useSiteContext } from "../Site";
import { VisualiserContext } from "../Visualiser";
import { classes, StyledButton, StyledLinkDialog } from "./styles";
import { sectionsMap } from "./common";
import type {
  ButtonLinkCalculatorProps,
  ButtonLinkDialogProps,
  ButtonLinkHubSpotProps,
  ButtonLinkLinkProps,
  ButtonLinkProps,
  ButtonLinkVisualiserProps
} from "./types";

const ExternalLink = ({
  to,
  hasBrandColours,
  className,
  gtm,
  children,
  ...rest
}: ButtonLinkLinkProps) => {
  return (
    <StyledButton
      className={classnames(className, hasBrandColours && classes.branded)}
      href={to}
      external
      gtm={{
        id: "cta-click1",
        action: to,
        label: children,
        ...gtm
      }}
      data-testid={`external-button-link-${replaceSpaces(children)}`}
      {...rest}
    >
      {transformHyphens(children)}
    </StyledButton>
  );
};

const InternalLink = ({
  to,
  hasBrandColours,
  className,
  gtm,
  children,
  ...rest
}: ButtonLinkLinkProps) => {
  const { countryCode } = useSiteContext();
  const url = getPathWithCountryCode(countryCode, to);

  return (
    <StyledButton
      className={classnames(className, hasBrandColours && classes.branded)}
      component={GatsbyLink}
      to={url}
      gtm={{
        id: "cta-click1",
        action: url,
        label: children,
        ...gtm
      }}
      data-testid={`internal-button-link-${replaceSpaces(children)}`}
      {...rest}
    >
      {transformHyphens(children)}
    </StyledButton>
  );
};

const AssetLink = ({
  to,
  hasBrandColours,
  className,
  gtm,
  children,
  ...rest
}: ButtonLinkLinkProps) => {
  return (
    <StyledButton
      className={classnames(className, hasBrandColours && classes.branded)}
      href={to}
      download
      external
      gtm={{
        id: "cta-click1",
        action: to,
        label: children,
        ...gtm
      }}
      data-testid={`asset-button-link-${replaceSpaces(children)}`}
      {...rest}
    >
      {transformHyphens(children)}
    </StyledButton>
  );
};

const VisualiserButton = ({
  parameters,
  onClick,
  hasBrandColours,
  className,
  children,
  gtm,
  ...rest
}: ButtonLinkVisualiserProps) => {
  const { open } = useContext(VisualiserContext);

  const handleOnClick = useCallback(
    (...args: unknown[]) => {
      onClick && onClick(...args);

      if (open) {
        open(parameters);
      }
    },
    [onClick, open, parameters]
  );

  return (
    <StyledButton
      className={classnames(className, hasBrandColours && classes.branded)}
      onClick={handleOnClick}
      gtm={{
        id: "cta-visualiser1",
        action: "visualiser",
        label: children,
        ...gtm
      }}
      data-testid={`visualiser-button-link-${replaceSpaces(children)}`}
      {...rest}
    >
      {transformHyphens(children)}
    </StyledButton>
  );
};

const CalculatorButton = ({
  onClick,
  hasBrandColours,
  className,
  children,
  gtm,
  ...rest
}: ButtonLinkCalculatorProps) => {
  const { open } = useContext(CalculatorContext);

  const handleOnClick = useCallback(
    (...args: unknown[]) => {
      onClick && onClick(...args);

      if (open) {
        open();
      }
    },
    [onClick, open]
  );

  return (
    <StyledButton
      className={classnames(className, hasBrandColours && classes.branded)}
      onClick={handleOnClick}
      gtm={{
        id: "cta-calculator1",
        action: "calculator",
        label: children,
        ...gtm
      }}
      data-testid={`calculator-button-link-${replaceSpaces(children)}`}
      {...rest}
    >
      {transformHyphens(children)}
    </StyledButton>
  );
};

const DialogButton = ({
  dialogContent,
  onClick,
  hasBrandColours,
  className,
  children,
  gtm,
  ...rest
}: ButtonLinkDialogProps) => {
  const [dialogIsOpen, setDialogIsOpen] = useState(false);

  const handleOnClick = useCallback(
    (...args: unknown[]) => {
      onClick && onClick(...args);
      setDialogIsOpen(true);
    },
    [onClick, setDialogIsOpen]
  );

  const handleDialogCloseClick = useCallback(() => {
    setDialogIsOpen(false);
  }, []);

  if (!dialogContent[0]) {
    return;
  }

  const Component: React.ElementType = sectionsMap["form-section"];

  return (
    <>
      <StyledButton
        className={classnames(className, hasBrandColours && classes.branded)}
        onClick={handleOnClick}
        gtm={{
          id: "cta-click1",
          action: "dialog",
          label: children,
          ...gtm
        }}
        data-testid={`dialog-button-link-${replaceSpaces(children)}`}
        {...rest}
      >
        {transformHyphens(children)}
      </StyledButton>
      <Dialog
        open={dialogIsOpen}
        onCloseClick={handleDialogCloseClick}
        data-testid={`dialog-container-${replaceSpaces(children)}`}
      >
        <StyledLinkDialog>
          <Suspense fallback={<div>loading...</div>}>
            <Component
              data={dialogContent[0]}
              id={`dialog-section-${uniqueId()}`}
              isDialog
            />
          </Suspense>
        </StyledLinkDialog>
      </Dialog>
    </>
  );
};

const HubSpotLink = ({
  hubSpotCtaId,
  hasBrandColours,
  className,
  gtm,
  children,
  ...rest
}: ButtonLinkHubSpotProps) => {
  const to = `${process.env.GATSBY_HUBSPOT_CTA_URL}${process.env.GATSBY_HUBSPOT_ID}/${hubSpotCtaId}`;
  return (
    <StyledButton
      className={classnames(className, hasBrandColours && classes.branded)}
      href={to}
      gtm={{
        id: "cta-click1",
        action: to,
        label: children,
        ...gtm
      }}
      data-testid={`hubspot-button-link-${replaceSpaces(children)}`}
      {...rest}
    >
      {transformHyphens(children)}
    </StyledButton>
  );
};

const ButtonLink = ({
  children,
  data,
  hasBrandColours,
  className,
  onClick,
  gtm,
  ...rest
}: ButtonLinkProps) => {
  switch (data.blockType) {
    case "external-link-block":
    case "rich-text-external-link-block":
      return (
        <ExternalLink
          to={data.url}
          hasBrandColours={hasBrandColours}
          className={className}
          gtm={gtm}
          {...rest}
        >
          {children}
        </ExternalLink>
      );
    case "internal-link-block":
    case "rich-text-internal-link-block":
      return (
        <InternalLink
          to={data.linkedPage.value.path}
          hasBrandColours={hasBrandColours}
          className={className}
          gtm={gtm}
          {...rest}
        >
          {children}
        </InternalLink>
      );
    case "asset-link-block":
    case "rich-text-asset-link-block":
      return (
        <AssetLink
          to={data.asset.url}
          hasBrandColours={hasBrandColours}
          className={className}
          gtm={gtm}
          {...rest}
        >
          {children}
        </AssetLink>
      );
    case "visualiser-link-block":
    case "rich-text-visualiser-link-block":
      return (
        <VisualiserButton
          onClick={onClick}
          parameters={data.parameters}
          hasBrandColours={hasBrandColours}
          className={className}
          gtm={gtm}
          {...rest}
        >
          {children}
        </VisualiserButton>
      );
    case "calculator-link-block":
    case "rich-text-calculator-link-block":
      return (
        <CalculatorButton
          onClick={onClick}
          hasBrandColours={hasBrandColours}
          className={className}
          gtm={gtm}
          {...rest}
        >
          {children}
        </CalculatorButton>
      );
    case "dialog-link-block":
    case "rich-text-dialog-link-block":
      return (
        <DialogButton
          onClick={onClick}
          dialogContent={data.dialogContent}
          hasBrandColours={hasBrandColours}
          className={className}
          gtm={gtm}
          {...rest}
        >
          {children}
        </DialogButton>
      );
    case "hubspot-link-block":
    case "rich-text-hubspot-cta-link-block":
      return (
        <HubSpotLink
          hubSpotCtaId={data.hubSpotCTAID}
          hasBrandColours={hasBrandColours}
          className={className}
          gtm={gtm}
          {...rest}
        >
          {children}
        </HubSpotLink>
      );
  }
};

export default ButtonLink;

export const query = graphql`
  fragment LinkFragmentNonRecursive on PayloadLink {
    ... on PayloadInternalLinkBlock {
      ...InternalLinkBlockFragment
    }
    ... on PayloadExternalLinkBlock {
      ...ExternalLinkBlockFragment
    }
    ... on PayloadHubSpotLinkBlock {
      ...HubSpotLinkBlockFragment
    }
    ... on PayloadDialogLinkBlock {
      blockType
      label
    }
    ... on PayloadVisualiserLinkBlock {
      ...PayloadVisualiserLinkBlockFragment
    }
    ... on PayloadCalculatorLinkBlock {
      ...PayloadCalculatorLinkBlockFragment
    }
    ... on PayloadAssetLinkBlock {
      ...AssetLinkBlockFragment
    }
  }
  fragment LinkFragment on PayloadLink {
    ...LinkFragmentNonRecursive
    ... on PayloadDialogLinkBlock {
      dialogContent {
        # Potential to add more sections here.
        ...FormDataFragmentNonRecursive
      }
    }
  }
`;
